import { LifeBuoy } from "lucide-react"
import { motion, useReducedMotion } from "motion/react"
import { useState } from "react"
import { useLocation } from "react-router"

import { AsistenteReporte } from "@/componentes/soporte/AsistenteReporte"
import { MisReportes } from "@/componentes/soporte/MisReportes"
import { SaludoDeSoporte } from "@/componentes/soporte/SaludoDeSoporte"
import { Cajon } from "@/componentes/ui/Cajon"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * El botón de reportar, siempre a mano.
 *
 * ⚠ **Flotante y en todas las pantallas internas a propósito.** Un canal de soporte
 * al que hay que llegar por un menú no lo usa nadie: cuando algo falla, la persona
 * está en medio de otra cosa y con prisa. Si reportar cuesta más que seguir a mano,
 * se sigue a mano y el fallo no se entera nadie.
 *
 * ⚠ **Fuera del portal del importador.** Sólo se monta en las rutas internas: la
 * evidencia que recoge —rutas, códigos, a veces valores de un formulario— no puede
 * cruzar la frontera de aislamiento por cliente, y el importador ya tiene el buzón
 * para hablar con su agente de cuenta.
 *
 * ⚠ **`tienePermiso` aquí es COSMÉTICO.** Esconde el botón a quien no puede
 * reportar; quien decide es el servidor. Ver `lib/autorizacion.ts`.
 */
export function BotonReportar() {
  const sesion = usarSesion((e) => e.usuario)
  const ubicacion = useLocation()
  const [abierto, setAbierto] = useState(false)
  const [vista, setVista] = useState<"reportar" | "mios">("reportar")
  const sinMovimiento = useReducedMotion()

  if (sesion === null || !tienePermiso(sesion, "reporte:crear")) return null
  // El portal tiene su propio canal. Y la pantalla pública de una tarea no tiene
  // sesión, así que ni llega aquí.
  if (ubicacion.pathname.startsWith("/portal")) return null

  return (
    <>
      {/* ⚠ **Sólo `transform` y `opacity`, y nada al refrescar datos.** Esto se
          monta una vez con el armazón y no vuelve a animarse: la regla de no animar
          filas al sondear vale igual aquí — un botón que late cada minuto en el
          rabillo del ojo es peor que uno quieto. */}
      <motion.button
        type="button"
        onClick={() => setAbierto(true)}
        aria-haspopup="dialog"
        data-testid="abrir-reporte"
        title="Reportar una falla del sistema"
        className="no-print flota-abajo fixed z-40 flex h-12 w-12 items-center justify-center rounded-full shadow-lg"
        style={{ backgroundColor: "var(--primary)", color: "var(--primary-fg)" }}
        initial={sinMovimiento ? false : { opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={sinMovimiento ? undefined : { scale: 1.06 }}
        whileTap={sinMovimiento ? undefined : { scale: 0.94 }}
        transition={
          sinMovimiento ? { duration: 0 } : { type: "spring", stiffness: 500, damping: 30 }
        }
      >
        <LifeBuoy size={20} aria-hidden={true} />
        <span className="sr-only">Reportar una falla</span>
      </motion.button>

      <SaludoDeSoporte onAbrir={() => setAbierto(true)} />

      <Cajon
        abierto={abierto}
        titulo={vista === "reportar" ? "Reportar una falla" : "Mis reportes"}
        onCerrar={() => setAbierto(false)}
      >
        {/* ⚠ **«Mis reportes» vive aquí y no en una pantalla aparte.** Es el mismo
            sitio donde se reporta, que es donde se vuelve a mirar: una entrada de
            menú para consultar lo que uno mandó no la encuentra nadie, y sin poder
            volver a mirarlo el canal es un buzón sin acuse. */}
        <div className="mb-4 flex gap-2">
          <button
            type="button"
            onClick={() => setVista("reportar")}
            aria-pressed={vista === "reportar"}
            data-testid="ver-reportar"
            className={
              vista === "reportar"
                ? "boton-primario h-8 px-3 text-xs"
                : "boton-secundario h-8 px-3 text-xs"
            }
          >
            Reportar una falla
          </button>
          <button
            type="button"
            onClick={() => setVista("mios")}
            aria-pressed={vista === "mios"}
            data-testid="ver-mis-reportes"
            className={
              vista === "mios"
                ? "boton-primario h-8 px-3 text-xs"
                : "boton-secundario h-8 px-3 text-xs"
            }
          >
            Mis reportes
          </button>
        </div>

        {/* Se remonta cada vez: un reporte a medio escribir de hace media hora
            llevaría señales viejas y un relato que ya no viene a cuento. */}
        {abierto && vista === "reportar" && (
          <AsistenteReporte key={String(abierto)} onCerrar={() => setAbierto(false)} />
        )}
        {abierto && vista === "mios" && <MisReportes />}
      </Cajon>
    </>
  )
}
